import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate } from "@tanstack/react-router";
import { Plus, XCircle } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { BasicModal } from "@/components/common/BasicModal";
import { Button } from "@/components/ui/button";
import { Form } from "@/components/ui/form";
import { Label } from "@/components/ui/label";
import { CHAT_MESSAGE_MAX_LENGTH, CHAT_TITLE_MAX_LENGTH } from "@/constants";
import { ChatForm } from "@/features/chat/components/ChatForm";
import { RecipientSelectorDialog } from "@/features/chat/components/RecipientSelectorDialog";
import { SelectedRecipients } from "@/features/chat/components/SelectedRecipients";
import { type CreateChatFormValues, createChatFormSchema } from "@/features/chat/schema";
import type { CreateChatModalProps } from "@/features/chat/types";
import { useCreateChatRoom } from "@/hooks/useCreateChatRoom";
import { useToast } from "@/hooks/useToast";
import type { User, UserShort } from "@/lib/humhub-api/types";

// User型をUserShort型に変換するヘルパー関数
const userToUserShort = (user: User): UserShort => ({
  id: user.id,
  guid: user.guid,
  display_name: user.display_name,
  url: user.url,
  image_url: user.image_url,
});

interface EnhancedCreateChatModalProps extends CreateChatModalProps {
  initialUser?: User;
}

export const CreateChatModal = ({
  isOpen,
  onClose,
  onChatCreated,
  initialUser,
}: EnhancedCreateChatModalProps) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const navigate = useNavigate();
  const { createChat, isLoading: isSubmitting, isError: hasError } = useCreateChatRoom();

  const [selectedRecipients, setSelectedRecipients] = useState<UserShort[]>([]);
  const [isRecipientDialogOpen, setIsRecipientDialogOpen] = useState(false);

  const chatFormSchema = useMemo(() => createChatFormSchema(t), [t]);

  const form = useForm<CreateChatFormValues>({
    resolver: zodResolver(chatFormSchema),
    defaultValues: {
      title: "",
      message: "",
      recipients: [],
    },
    mode: "onChange",
  });

  const {
    handleSubmit,
    reset,
    setValue,
    formState: { isDirty, isValid },
  } = form;

  useEffect(() => {
    if (isOpen) {
      const initialRecipients = initialUser ? [userToUserShort(initialUser)] : [];
      setSelectedRecipients(initialRecipients);
      reset({
        title: "",
        message: "",
        recipients: initialRecipients.map((recipient) => recipient.guid),
      });
    }
  }, [isOpen, initialUser, reset]);

  const handleRecipientsSelected = (recipients: UserShort[]) => {
    setSelectedRecipients(recipients);
    setValue(
      "recipients",
      recipients.map((recipient) => recipient.guid),
      { shouldDirty: true, shouldValidate: true },
    );
    setIsRecipientDialogOpen(false);
  };

  const handleRecipientRemove = (recipientToRemove: UserShort) => {
    const updatedRecipients = selectedRecipients.filter(
      (recipient) => recipient.guid !== recipientToRemove.guid,
    );
    setSelectedRecipients(updatedRecipients);
    setValue(
      "recipients",
      updatedRecipients.map((recipient) => recipient.guid),
      { shouldDirty: true, shouldValidate: true },
    );
  };

  const onSubmitForm = handleSubmit(async (values) => {
    try {
      const response = await createChat({
        title: values.title,
        message: values.message,
        recipient: values.recipients,
      });

      if (onChatCreated) {
        await onChatCreated();
      }

      toast({
        title: t("chat.createSuccess"),
        description: t("chat.createSuccessDescription"),
      });

      onClose();

      if (response?.id) {
        navigate({
          to: "/chat-room/$chat-room-id",
          params: { "chat-room-id": response.id.toString() },
        });
      }
    } catch (error) {
      console.error("Failed to create chat:", error);
    }
  });

  return (
    <>
      <BasicModal isOpen={isOpen} onClose={onClose} maxWidth="max-w-2xl">
        <Form {...form}>
          <form onSubmit={onSubmitForm} className="mx-auto max-w-2xl space-y-6 p-6">
            <h2 className="text-center font-bold text-2xl">{t("chat.createNewChatRoom")}</h2>

            <div className="space-y-6">
              <div className="space-y-2">
                <div className="flex items-center justify-start">
                  <Label htmlFor="recipients">{t("chat.recipients")} *</Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setIsRecipientDialogOpen(true)}
                    disabled={isSubmitting}
                  >
                    <Plus size={16} />
                    {t("chat.selectRecipients")}
                  </Button>
                </div>

                <SelectedRecipients
                  recipients={selectedRecipients}
                  onRemove={handleRecipientRemove}
                  showTitle={false}
                />
              </div>

              <ChatForm
                form={form}
                titleMaxLength={CHAT_TITLE_MAX_LENGTH}
                messageMaxLength={CHAT_MESSAGE_MAX_LENGTH}
                disabled={isSubmitting}
              />
            </div>

            {hasError && (
              <div className="rounded-md bg-red-50 p-3">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <XCircle className="h-5 w-5 text-red-400" />
                  </div>
                  <div className="ml-3">
                    <h3 className="font-medium text-red-800 text-sm">{t("chat.createError")}</h3>
                    <p className="mt-1 text-red-700 text-sm">{t("chat.createErrorDescription")}</p>
                  </div>
                </div>
              </div>
            )}

            <div className="flex justify-between">
              <Button variant="ghost" onClick={onClose} disabled={isSubmitting}>
                {t("common.cancel")}
              </Button>
              <Button type="submit" variant="teal" disabled={isSubmitting || !isDirty || !isValid}>
                {isSubmitting ? t("common.saving") : t("common.create")}
              </Button>
            </div>
          </form>
        </Form>
      </BasicModal>

      <RecipientSelectorDialog
        open={isRecipientDialogOpen}
        onClose={() => setIsRecipientDialogOpen(false)}
        onRecipientsSelected={handleRecipientsSelected}
        initialRecipients={selectedRecipients}
      />
    </>
  );
};
